import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSessionContext } from '@supabase/auth-helpers-react';
import { Check, CircleCheckBig, Clock, CreditCard, Crown, Hourglass, ShieldCheck, ExternalLink } from 'lucide-react';
import storage from 'local-storage-fallback';

import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import EhAuthDialog from '../components/EhAuthDialog';

import { EhPaymentSuccessDialog } from '../components/EhPaymentSuccessDialog';
import { EhPaymentErrorDialog } from '../components/EhPaymentErrorDialog';
import { supabaseEh, supabase } from '../lib/supabase';
import { generateBase58Id, TABLE_PREFIXES } from '../lib/id';
import { cn, formatDate } from '../lib/utils';
import { loadAtlosScript, checkPayment, getPaymentEmail } from '../lib/ehAtlosPayment';
import { SEO } from '../components/SEO';
import { useTracking, PAYMENT_EVENTS, USER_EVENTS } from '../hooks/useTracking';
import { LS_DISCOUNT_TIMER_KEY } from '@/lib/constants';
import { Database } from '@/types/database';
import { EHUserAppMetadata, SubscriptionMetadata } from '@/lib/types';
import { useUserInfo } from '@/hooks/useUserInfo';
import { useGemBalance } from '@/hooks/useGemBalance';
import {
  generateOrderId,
  getExtendedSubscriptionEndDate,
  getStripeBillingPortalUrl,
  getStripeConfig,
  getSubscriptionPlans,
  STRIPE_PAYEMENT_BASE_URL,
  SubscriptionPlan,
} from '../lib/ehPrices';
import {
  isFromAppleAppstore,
  isFromAppStores,
  isHidingMonetizationInStores,
  // isHidingExternalPaymentCta,
  isTwaOrPlayStoreInstalled,
  isTwaSession,
} from '@/lib/detection';
import { EhEndorselyScript } from '@/components/EhEndorselyScript';
import { usePlayBilling } from '@/hooks/usePlayBilling';
import { PlayProductDetails } from '@/hooks/usePlayBilling';
import { useAppleStoreKit, SwiftPurchaseState } from '@/hooks/useAppleStoreKit';

type Subscription = Database['eh']['Tables']['subscriptions']['Row'];

export default function SubscriptionPage() {
  const { session, isLoading: isSessionLoading } = useSessionContext();
  const navigate = useNavigate();
  const { trackEvent } = useTracking();
  const { isRenewableSubscriber, isUnlimited, refetchUserInfo, subscriptions, user, userDetails } = useUserInfo();
  const { refreshBalance } = useGemBalance();

  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [paymentCheckStatus, setPaymentCheckStatus] = useState<'' | 'pending' | 'verified' | 'failed'>('');
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [activeSubscription, setActiveSubscription] = useState<{
    id: string;
    plan: string;
    endDate: string;
    stripeSubscriptionId: string | null;
    subMetadata: SubscriptionMetadata;
  } | null>(null);
  const [extendedEndDate, setExtendedEndDate] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<{ minutes: number; seconds: number } | null>(null);
  const [showDiscountBanner, setShowDiscountBanner] = useState(false);
  const [hadPreviousSubscription, setHadPreviousSubscription] = useState(true); // to hide timer by default
  const currentOrderIdRef = useRef<string | null>(null);
  const atlosRetries = useRef(0);
  const isMountedRef = useRef(false);
  
  // Stripe payment states
  const [isAwaitingStripePayment, setIsAwaitingStripePayment] = useState(false);
  const [cardPaymentUrl, setCardPaymentUrl] = useState<string>('');
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<'crypto' | 'card' | 'play_billing' | 'apple_store' | null>(null);
  const stripePaymentTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const realtimeSubRef = useRef<{ unsubscribe: () => void } | null>(null);
  const draftSubscriptionIdRef = useRef<string | null>(null);
  
  // Mobile stores
  const {
    isSupported: isAppleStoreKitSupported,
    products: appleStoreProducts,
    getProductDetails: getAppleProductDetails,
    purchase: purchaseWithAppleStoreKit,
    manageSubscriptions: manageAppleSubscriptions,
    storefrontCountry
  } = useAppleStoreKit();
  const [isRestoringApplePurchase, setIsRestoringApplePurchase] = useState<boolean | null>(null);
  const {
    isSupported: isPlayBillingSupported, 
    getProductDetails, 
    purchase: purchaseWithPlayBilling 
  } = usePlayBilling();
  const [playStoreProduct, setPlayStoreProduct] = useState<PlayProductDetails & { localizedPrice: string } | null>(null);
  const [isPlayStoreInstalled, setIsPlayStoreInstalled] = useState(false);

  const subscriptionPlans = getSubscriptionPlans().sort((a, b) => a.price - b.price);

  // Apple subscription plans - filter existing plans that have appleStoreSku
  const appleSubscriptionPlans: SubscriptionPlan[] = useMemo(() => subscriptionPlans
    .filter(plan => plan.appleStoreSku)
    .map(plan => ({
      ...plan,
      appleStoreSku: plan.appleStoreSku!,
      discount: 0,
      originalPrice: 0,
      discountedPrice: 0,
      billedAmount: 0,
    })), [subscriptionPlans]);
  
  // Check if the user has an active subscription
  const checkActiveOrPastSubscription = useCallback(async () => {
    try {
      // sort subs by descending end_at
      const subs = subscriptions.filter(s => !!s.end_at).sort((a, b) => b.end_at!.localeCompare(a.end_at!));
      // only consider real (renewable) subscriptions, not one-time packages
      setHadPreviousSubscription(subs && subs.filter(s => s.s_subscription_id && s.status !== 'draft').length > 0);
      
      const activeSubscription = subs.filter(s => s.status === 'active')[0];
      if (activeSubscription && activeSubscription.end_at && activeSubscription.type) {
        setShowErrorDialog(false); // if shown previously after timeout, hide it
        setActiveSubscription({
          id: activeSubscription.id,
          plan: activeSubscription.type,
          endDate: activeSubscription.end_at,
          subMetadata: activeSubscription.sub_metadata as SubscriptionMetadata,
          stripeSubscriptionId: activeSubscription.s_subscription_id,
        });
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error:', error);
    }
  }, [subscriptions]);
  
  // Check if the user is authenticated
  useEffect(() => {
    if (!isSessionLoading) {
      if (!session) {
        // Redirect to home if not authenticated
        navigate('/');
      } else {
        // Check if user has an active or past subscription
        void checkActiveOrPastSubscription();
      }
      setIsLoading(false);
    }
  }, [checkActiveOrPastSubscription, session, isSessionLoading, navigate]);
  
  // refresh balance and load Atlos script on load
  useEffect(() => {
    if (!isLoading && session) {
      void refreshBalance();
      void loadAtlosScript();
    }
  }, [isLoading, session, refreshBalance]);

  // Fetch Play Store product details
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (isPlayBillingSupported) {
        setIsPlayStoreInstalled(await isTwaOrPlayStoreInstalled());

        const unlimitedSku = import.meta.env.VITE_PLAY_72H_UNLIMITED_SKU;
        if (unlimitedSku) {
          const details = await getProductDetails([unlimitedSku]);
          if (details && details.length > 0) {
            const localizedPrice = new Intl.NumberFormat(
              navigator.language,
              {style: 'currency', currency: details[0].price.currency}
            ).format(details[0].price.value);
            setPlayStoreProduct({
              ...details[0],
              localizedPrice,
            });
            if (import.meta.env.DEV) console.log('Play Store product details:', details[0]);
          }
        }
      }
    };
    fetchProductDetails();
  }, [isPlayBillingSupported, getProductDetails]);
  
  // Fetch Apple App Store product details
  useEffect(() => {
    const fetchAppleProductDetails = async () => {
      if (isAppleStoreKitSupported) {
        const subscriptionSkus = appleSubscriptionPlans.map(plan => plan.appleStoreSku!);
        if (subscriptionSkus.length > 0) {
          await getAppleProductDetails(subscriptionSkus);
        }
      }
    };
    fetchAppleProductDetails();
  }, [appleSubscriptionPlans, isAppleStoreKitSupported, getAppleProductDetails]);

  // Reset extended end date if plan changes
  useEffect(() => {
    if (!selectedPlan?.hasMonthlyMultiplePeriod) return;
    
    if (selectedPlan && activeSubscription) {
      const extended = getExtendedSubscriptionEndDate(activeSubscription.endDate, selectedPlan.period);
      setExtendedEndDate(formatDate(extended));
    } else {
      setExtendedEndDate(null);
    }
  }, [selectedPlan, activeSubscription]);
  
  // Handle discount timer
  useEffect(() => {
    const shouldShowTimer = () => {
      // Don't show timer for users with active subscription
      if (activeSubscription && isRenewableSubscriber) return 0;
      
      // Check if timer is expired or not set
      const timerStartTime = storage.getItem(LS_DISCOUNT_TIMER_KEY);
      if (!timerStartTime) return 1;
      
      const startTime = parseInt(timerStartTime, 10);
      const now = Date.now();
      const hoursPassed = (now - startTime) / (1000 * 60 * 60);
      
      // Show if more than 24 hours since last timer, or less than 45 minutes
      if (hoursPassed >= 24) return 1;
      if (hoursPassed <= 0.75) return startTime;
      
      return 0;
    };
    
    const initialTime = shouldShowTimer();
    if (!isLoading && initialTime && !hadPreviousSubscription) {
      // Set timer start if not exists or obsolete
      if (!storage.getItem(LS_DISCOUNT_TIMER_KEY) || initialTime === 1) {
        storage.setItem(LS_DISCOUNT_TIMER_KEY, Date.now().toString());
      }
      
      if (import.meta.env.DEV) {
        console.log(`[DEV] last timer start: ${initialTime} (${new Date(initialTime).toLocaleString()})`);
      }
      // 45 minutes if obsolete timer (1), or minutes since last timer started less than 45 minutes ago
      const minutesRemaining = initialTime === 1 ? 45 : 45 - (Date.now() - initialTime) / (1000 * 60);
      const secondsRemaining = initialTime === 1 ? 0 : Math.round((minutesRemaining - Math.floor(minutesRemaining)) * 60);

      setTimeRemaining({ minutes: Math.floor(minutesRemaining), seconds: secondsRemaining });
      setShowDiscountBanner(true);
      
      // Start countdown
      const interval = setInterval(() => {
        setTimeRemaining(prevTime => {
          if (!prevTime) return null;
          
          let minutes = prevTime.minutes;
          let seconds = prevTime.seconds - 1;
          
          if (seconds < 0) {
            seconds = 59;
            minutes -= 1;
          }
          
          if (minutes < 0) {
            clearInterval(interval);
            setShowDiscountBanner(false);
            return null;
          }
          
          return { minutes, seconds };
        });
      }, 1000);
      
      return () => clearInterval(interval);
    } else if (hadPreviousSubscription) {
      setShowDiscountBanner(false);
    }
  }, [isLoading, activeSubscription, hadPreviousSubscription, isRenewableSubscriber]);
  
  // Cleanup realtime subscription on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      // realtimeSubRef.current?.unsubscribe(); // don't unsubscribe, so we can detect sub even after leaving page
      if (stripePaymentTimeoutRef.current) {
        clearTimeout(stripePaymentTimeoutRef.current);
      }
    };
  }, []);

  const buildCCPaymentUrl = useCallback((plan: SubscriptionPlan, clientRefId: string) => {
    const stripeConfig = getStripeConfig(plan);
    let paymentUrl = `${STRIPE_PAYEMENT_BASE_URL}${stripeConfig.paymentLink}?client_reference_id=${clientRefId}`;
  
    if (stripeConfig.promoCode) {
      paymentUrl = `${paymentUrl}&prefilled_promo_code=${stripeConfig.promoCode}`;
    }
    // Add prefilled email in production
    if (user?.email && !import.meta.env.DEV) {
      paymentUrl = `${paymentUrl}&prefilled_email=${encodeURIComponent(user.email)}`;
    }
    return paymentUrl;
  }, [user?.email]);

  const createOrUpdateDraftSubscription = useCallback(async (plan: SubscriptionPlan, userId: string): Promise<string> => {
    // Find the most recent draft subscription for this user, created <24h ago
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    let draftSubId = generateBase58Id(TABLE_PREFIXES.subscriptions);

    try {
      const { data: drafts, error } = await supabaseEh
        .from('subscriptions')
        .select('id, type, created_at')
        .eq('user_id', userId)
        .eq('status', 'draft')
        .gte('created_at', oneDayAgo)
        .order('created_at', { ascending: false })
        .limit(1);

      if (!error && drafts && drafts.length > 0) {
        draftSubId = drafts[0].id;
        // Only update if plan changed
        if (drafts[0].type !== plan.id) {
          await supabaseEh
            .from('subscriptions')
            .update({
              type: plan.id,
              sub_metadata: {
                provider: 'stripe',
                planId: plan.id,
                amount: plan.billedAmount,
                stripeConfig: getStripeConfig(plan),
              },
              updated_at: new Date().toISOString(),
            })
            .eq('id', draftSubId);
        }
      } else { // No recent draft, create new
        let userDetailsId = userDetails?.id || (user?.app_metadata as EHUserAppMetadata)?.user_details_id;

        if (!userDetailsId) { // should not happen (created by database webhook)
          trackEvent(USER_EVENTS.USER_DETAILS_ID_NOT_FOUND, {
            error: 'User details ID not found before creating draft subscription',
            userId,
            userDetailsId,
            userAppMetadata: user?.app_metadata,
          });
          // fallback: fetch fresh user
          const { data: userData } = await supabase.auth.getUser();
          if (userData?.user?.app_metadata?.user_details_id) {
            userDetailsId = userData.user.app_metadata.user_details_id;
          } else {
            trackEvent(USER_EVENTS.USER_DETAILS_ID_NOT_FOUND, {
              error: 'User details ID not found after fetching fresh user',
              userId,
              userAppMetadata: userData?.user?.app_metadata,
              isRetry: true,
            });
          }
          // Proceed anyway
        }

        const now = new Date();

        await supabaseEh
          .from('subscriptions')
          .insert({
            id: draftSubId,
            user_id: userId,
            user_details_id: userDetailsId,
            type: plan.id,
            status: 'draft',
            start_at: now.toISOString(),
            end_at: now.toISOString(), // Will be updated by webhook
            sub_metadata: {
              provider: 'stripe',
              planId: plan.id,
              amount: plan.billedAmount,
              stripeConfig: getStripeConfig(plan),
            },
          });
      }
    } catch (e) {
      trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
        paymentMethod: 'card',
        plan: plan,
        billedAmount: plan.billedAmount,
        error: `Failed to create or update draft subscription: ${e instanceof Error ? e.message : String(e)}`,
        source: 'SubscriptionPage.createOrUpdateDraftSubscription',
      });
    }
    return draftSubId;
  }, [trackEvent, user, userDetails?.id]);

  // Set initial selected plan and draft subscription
  useEffect(() => {
    const defaultPlan = subscriptionPlans.find(p => p.id === 'quarterly') || subscriptionPlans.filter(p => p.hasMonthlyMultiplePeriod)[0];
    const setupDraftSubscription = async (plan: SubscriptionPlan = defaultPlan) => {
      if (!user) return;
      if (draftSubscriptionIdRef.current) {
        // set new url before async request in case the user clicks pay button immediately
        setCardPaymentUrl(buildCCPaymentUrl(plan, draftSubscriptionIdRef.current));
      }
      // Create or reuse draft subscription
      const draftId = await createOrUpdateDraftSubscription(plan, user.id);
      draftSubscriptionIdRef.current = draftId;
      setCardPaymentUrl(buildCCPaymentUrl(plan, draftId));
    };

    if (subscriptionPlans.length > 0 && !selectedPlan) {
      // Default to the quarterly option if available
      setSelectedPlan(defaultPlan);
    }
    void setupDraftSubscription(selectedPlan ?? defaultPlan);
  }, [buildCCPaymentUrl, createOrUpdateDraftSubscription, subscriptionPlans, selectedPlan, user]);

  const subscribeToRealtimeUpdates = async (userId: string) => {
    realtimeSubRef.current?.unsubscribe();

    await supabase.realtime.setAuth() // Realtime Authorization
    const changes =supabase
      .channel(`subscriptions:${userId}`, {
        config: { private: true },
      })
      .on(
        'broadcast',
        { event: '*' },
        ({ event, payload }) => {
          if (import.meta.env.DEV) {
            console.log(`[DEV] Subscription change received: ${event}`, payload);
          }

          // Ignore DELETE events
          // One-time package can either be inserted as new subscription or updated as extension
          // if an unlimited sub already exists
          if (!['INSERT', 'UPDATE'].includes(event)) return;
          
          const updatedSubscription = payload?.record as Subscription;

          if (updatedSubscription?.status === 'active') {
            if (isMountedRef.current) { // do not update state of unmounted component
              setIsAwaitingStripePayment(false);
              setPaymentCheckStatus('verified');
              
              // Update local subscription state
              setActiveSubscription({
                id: updatedSubscription.id,
                plan: updatedSubscription.type || '',
                endDate: updatedSubscription.end_at || '',
                stripeSubscriptionId: updatedSubscription.s_subscription_id,
                subMetadata: updatedSubscription.sub_metadata as SubscriptionMetadata,
              });
  
              if (updatedSubscription.end_at) {
                setExtendedEndDate(formatDate(updatedSubscription.end_at));
              }
  
              // Show success dialog
              setShowSuccessDialog(true);
              setShowErrorDialog(false);
            }

            // Track successful payment
            if (selectedPlan) {
              trackEvent(PAYMENT_EVENTS.PAYMENT_COMPLETED, {
                paymentMethod: 'card',
                plan: selectedPlan,
                billedAmount: selectedPlan.billedAmount,
                subscriptionId: updatedSubscription.id,
                isExtension: !!activeSubscription
              });

              trackEvent(PAYMENT_EVENTS.SUBSCRIPTION_STARTED, {
                plan: selectedPlan,
                billedAmount: selectedPlan.billedAmount,
                subscriptionId: updatedSubscription.id,
                isExtension: !!activeSubscription,
                subscriptionEndDate: updatedSubscription.end_at
              });
            }

            void refetchUserInfo(); // for subscription data
            void refreshBalance();

            // Cleanup
            if (realtimeSubRef.current) {
              realtimeSubRef.current.unsubscribe();
            }
            if (stripePaymentTimeoutRef.current) {
              clearTimeout(stripePaymentTimeoutRef.current);
              stripePaymentTimeoutRef.current = null;
            }
          }
        }
      )
      .subscribe();

    const unsubscribe = () => {
      supabase.removeChannel(changes);
      realtimeSubRef.current = null;
    };
    realtimeSubRef.current = { unsubscribe };

    // Set 30-minute timeout
    const timeout = setTimeout(() => {
      handleStripePaymentTimeout();
    }, 30 * 60 * 1000); // 30 minutes

    stripePaymentTimeoutRef.current = timeout;
  };

  const handleStripePaymentTimeout = () => {
    setIsAwaitingStripePayment(false);
    setPaymentCheckStatus('failed');
    setErrorMessage('Payment verification timed out. If you completed the payment, it will be processed in the background.');
    setShowErrorDialog(true);

    // Cleanup
    realtimeSubRef.current?.unsubscribe();
    if (stripePaymentTimeoutRef.current) {
      clearTimeout(stripePaymentTimeoutRef.current);
      stripePaymentTimeoutRef.current = null;
    }
  };

  const cancelStripePayment = () => {
    setIsAwaitingStripePayment(false);
    
    // Track cancellation
    if (selectedPlan && draftSubscriptionIdRef.current) {
      trackEvent(PAYMENT_EVENTS.PAYMENT_CANCELED, {
        paymentMethod: 'card',
        plan: selectedPlan,
        billedAmount: selectedPlan.billedAmount,
        subscriptionId: draftSubscriptionIdRef.current,
        isExtension: !!activeSubscription,
      });
    }

    // Cleanup
    realtimeSubRef.current?.unsubscribe();
    if (stripePaymentTimeoutRef.current) {
      clearTimeout(stripePaymentTimeoutRef.current);
      stripePaymentTimeoutRef.current = null;
    }
  };

  const handleAuthDialogOpenChange = (open: boolean) => {
    setShowAuthDialog(open);
  };

  const handlePlanSelect = (plan: SubscriptionPlan) => {
    // automatically proceed to payment if plan is already selected
    if (selectedPlan?.id === plan.id) {
      if (isAppleStoreKitSupported) {
        handleAppleStorePayment(plan);
      } else {
        handleCCPayment();
      }
      return;
    }
    setSelectedPlan(plan);
  };
  
  const handleCryptoPayment = () => {
    if (!selectedPlan) {
        trackEvent(PAYMENT_EVENTS.PAYMENT_CLICKED, {
          paymentMethod: 'crypto',
          noSelectedPlan: true,
          isExtension: !!activeSubscription
      });
      return;
    }
    
    // If user is not logged in, show auth dialog
    if (!user) {
      setShowAuthDialog(true);
      return;
    }
    
    setIsProcessing(true);
    setCurrentPaymentMethod('crypto');

    // Generate a unique order ID
    const orderId = generateOrderId();

    trackEvent(PAYMENT_EVENTS.PAYMENT_CLICKED, {
      paymentMethod: 'crypto',
      plan: selectedPlan,
      billedAmount: selectedPlan.billedAmount,
      orderId: !window.atlos ? 'pending' : orderId,
      isExtension: !!activeSubscription
    });
    
    if (!window.atlos) {
      if (atlosRetries.current > 5) {
        const errorMessage = 'Payment system is not loaded. Please try again or refresh the page.';
        setErrorMessage(errorMessage);
        setIsProcessing(false);
        setShowErrorDialog(true);
        trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
          paymentMethod: 'crypto',
          plan: selectedPlan,
          billedAmount: selectedPlan.billedAmount,
          orderId: orderId,
          isExtension: !!activeSubscription,
          error: `Failed to load atl payment library: '${errorMessage}'`
        });
        return;
      }
      void loadAtlosScript();
      setTimeout(() => {
        atlosRetries.current++;
        handleCryptoPayment();
      }, 3000);
      return;
    }

    currentOrderIdRef.current = orderId;
    
    // Handle Atlos payment
    window.atlos.Pay({
      merchantId: import.meta.env.VITE_ATL_MERCHANT_ID as string,
      orderId: orderId,
      orderAmount: selectedPlan.billedAmount,
      orderCurrency: 'USD',
      userEmail: getPaymentEmail(user.email || ''),
      onSuccess: (data) => handleCryptoPaymentSuccess(selectedPlan, orderId, data),
      onCanceled: () => handlePaymentCanceled(),
      onCompleted: () => handlePaymentCompleted(),
      theme: 'dark'
    });
  };
  
  const handleCryptoPaymentSuccess = async (plan: SubscriptionPlan, orderId: string, data: string) => {
    if (import.meta.env.DEV) {
      console.log('[DEV] Payment success:', data);
    }
    
    try {
      setPaymentCheckStatus('pending');
      const responseData = await checkPayment(plan, orderId, user?.id);
      setPaymentCheckStatus('verified');
      
      // Track payment completed event
      trackEvent(PAYMENT_EVENTS.PAYMENT_COMPLETED, {
        paymentMethod: 'crypto',
        plan,
        billedAmount: plan.billedAmount,
        orderId: orderId,
        isExtension: !!activeSubscription
      });
      
      // Track subscription started event
      trackEvent(PAYMENT_EVENTS.SUBSCRIPTION_STARTED, {
        plan,
        billedAmount: plan.billedAmount,
        orderId: orderId,
        isExtension: !!activeSubscription,
        subscriptionEndDate: responseData.subscriptionExpDate
      });
      
      // Set extended end date if available from response
      if (responseData.subscriptionExpDate) {
        setExtendedEndDate(formatDate(responseData.subscriptionExpDate));
      }

      await refetchUserInfo(); // for subscription data
      await refreshBalance();
      
      // Refresh subscription data
      void checkActiveOrPastSubscription();
    } catch (error) {
      console.error('Payment processing error:', error);
      setPaymentCheckStatus('failed');

      trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
        paymentMethod: 'crypto',
        plan,
        billedAmount: plan.billedAmount,
        orderId: orderId,
        isExtension: !!activeSubscription,
        error: error instanceof Error ? error.message : String(error)
      });
    } finally {
      setIsProcessing(false);
      setShowSuccessDialog(true);
    }
  };
  
  const handlePaymentCanceled = () => {
    if (import.meta.env.DEV) {
      console.log('[DEV] Payment canceled');
    }

    if (selectedPlan && currentOrderIdRef.current) {
      trackEvent(PAYMENT_EVENTS.PAYMENT_CANCELED, {
        paymentMethod: 'crypto',
        plan: selectedPlan,
        billedAmount: selectedPlan.billedAmount,
        orderId: currentOrderIdRef.current,
        isExtension: !!activeSubscription,
      });
    }
    
    setIsProcessing(false);
  };
  
  const handlePaymentCompleted = () => {
    setIsProcessing(false);
  };
  
  const retryPaymentCheck = async () => {
    if (!selectedPlan || !currentOrderIdRef.current || !user?.id) return;
    
    try {
      setPaymentCheckStatus('pending');
      const responseData = await checkPayment(selectedPlan, currentOrderIdRef.current, user.id);
      setPaymentCheckStatus('verified');
      
      // Track payment completed on retry
      trackEvent(PAYMENT_EVENTS.PAYMENT_COMPLETED, {
        paymentMethod: 'crypto',
        plan: selectedPlan,
        billedAmount: selectedPlan.billedAmount,
        orderId: currentOrderIdRef.current,
        isExtension: !!activeSubscription,
        isRetry: true
      });

      await refetchUserInfo(); // for subscription data
      await refreshBalance();
      
      // Refresh subscription data
      void checkActiveOrPastSubscription();
      
      // Set extended end date if available from response
      if (responseData.subscriptionExpDate) {
        setExtendedEndDate(formatDate(responseData.subscriptionExpDate));
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('[DEV] Payment verification retry failed:', error);
      setPaymentCheckStatus('failed');
      setErrorMessage('Payment verification failed.');

      trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
        paymentMethod: 'crypto',
        plan: selectedPlan,
        billedAmount: selectedPlan.billedAmount,
        orderId: currentOrderIdRef.current,
        error: error instanceof Error ? error.message : String(error),
        isExtension: !!activeSubscription,
        isRetry: true,
      });
      
      setShowSuccessDialog(false);
      setShowErrorDialog(true);
    }
  };
  
  const retryCCPayment = () => {
    if (!selectedPlan || !draftSubscriptionIdRef.current || !user?.id) return;
    
    // Create payment link with client reference ID
    const paymentUrl = cardPaymentUrl;

    // Open payment link in new tab
    window.open(paymentUrl, '_blank', 'noopener,noreferrer');

    // Start realtime monitoring again
    void subscribeToRealtimeUpdates(user.id);
    setIsAwaitingStripePayment(true);
    setShowErrorDialog(false);
  };

  const hasActiveStripeSubscription = activeSubscription?.subMetadata?.provider === 'stripe' && !!activeSubscription.stripeSubscriptionId;

  const unlimitedPlan: SubscriptionPlan = subscriptionPlans.find(p => p.id === '72h-unlimited')!;

  const handlePlayBillingPayment = async () => {
    if (!playStoreProduct) return;
    setCurrentPaymentMethod('play_billing');
    setIsProcessing(true);
    const { success } = await purchaseWithPlayBilling({ sku: playStoreProduct.itemId, userId: user?.id });
    if (success) {
      // The backend handles subscription creation. We can show a success message.
      setShowSuccessDialog(true);
      await refetchUserInfo();
      await refreshBalance();
    } else {
      // Error is handled and displayed by the hook
    }
    setIsProcessing(false);
  };

  const handleAppleStorePayment = async (plan: Pick<SubscriptionPlan, 'id' | 'name' | 'period' | 'price' | 'appleStoreSku' | 'hasMonthlyMultiplePeriod'>) => {
    if (!appleStoreProducts.length) return;
    const appleErrorMessage = 'Sorry, an error happened. Please try again later, our team is working on it.'
    
    // Find the corresponding Apple product for formatted pricing
    const appleProduct = appleStoreProducts?.find(product => 
      product.attributes?.offerName === plan.appleStoreSku
    );
    
    if (!appleProduct?.attributes?.offerName || !plan.appleStoreSku) {
      setErrorMessage(appleErrorMessage);
      setShowErrorDialog(true);
      return;
    }

    setCurrentPaymentMethod('apple_store');
    setIsProcessing(true);

    // Show Apple-managed subscription management sheet in iOS
    if (activeSubscription?.subMetadata?.provider === 'appleStore' && plan.hasMonthlyMultiplePeriod) {
      await manageAppleSubscriptions();
      setIsProcessing(false);
      return;
    }

    const onResult = (status: SwiftPurchaseState) => {
      if (['failed', 'notFound'].includes(status)) {
        setErrorMessage(appleErrorMessage);
        setShowErrorDialog(true);
        return;
      }

      // status === 'success'
      // The actual subscription will be created via Apple Server Notifications.
      // setShowSuccessDialog(true); // handled by subscribeToRealtimeUpdates
      setShowErrorDialog(false);
      
      // Set up realtime monitoring for Apple transaction processing
      if (user?.id) {
        void subscribeToRealtimeUpdates(user.id);
      }
      
      // Refresh user info and balance after a delay if realtime is not working
      setTimeout(async () => {
        await refetchUserInfo();
        await refreshBalance();
      }, 5000);
    };

    try {
      await purchaseWithAppleStoreKit({
        onResult,
        sku: plan.appleStoreSku,
        userId: user?.id
      });
    } catch (error) {
      setErrorMessage(appleErrorMessage);
      setShowSuccessDialog(false);
      setShowErrorDialog(true);
      trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
        paymentMethod: 'appleStore',
        error: `SubscriptionPage.handleAppleSubscriptionPayment failed: ${error instanceof Error ? error.message : String(error)}`,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOneTimePayment = (plan: SubscriptionPlan = unlimitedPlan) => {
    // don't hide standard payment option if not in app supporting play billing
    if (isPlayStoreInstalled && isPlayBillingSupported) {
      handlePlayBillingPayment();
      return;
    }
    if (isFromAppleAppstore() && isAppleStoreKitSupported) {
      handleAppleStorePayment(plan);
      return;
    }
    if (plan.hasMonthlyMultiplePeriod) {
      if (import.meta.env.DEV) console.error('[DEV] Error: one-time payment with monthly multiple period plan');
      return;
    }
    setSelectedPlan(plan);
    // pass plan to make sure we don't create a draft sub due to new selected plan not rendered yet
    handleCCPayment(plan);
  }

  const handleCCPayment = async (p?: SubscriptionPlan) => {
    const plan = p ?? selectedPlan;
    if (!plan) {
      trackEvent(PAYMENT_EVENTS.PAYMENT_CLICKED, {
        paymentMethod: 'card',
        noSelectedPlan: true,
        isExtension: !!activeSubscription
      });
      return;
    }

    if (hasActiveStripeSubscription) {
      let url = getStripeBillingPortalUrl(user?.email);
      if (!url) { // fallback to stripe checkout page that will show active account
        url = buildCCPaymentUrl(plan, activeSubscription?.id);
        trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
          paymentMethod: 'card',
          error: 'Failed to get stripe billing portal url',
          source: 'SubscriptionPage.handleCCPayment',
        });
      }
      setCurrentPaymentMethod('card');
      setCardPaymentUrl(url);
      window.open(url, '_blank', 'noopener,noreferrer');
      trackEvent(PAYMENT_EVENTS.STRIPE_BILLING_PORTAL_OPENED, {
        paymentMethod: 'card',
        billingPortal: true,
      });
      return;
    }
    if (!user) {
      setShowAuthDialog(true);
      return;
    }
    setCurrentPaymentMethod('card');

    const subscribeAndTrackPayment = (userId: string) => {
      // Start realtime monitoring
      void subscribeToRealtimeUpdates(userId);
      setIsAwaitingStripePayment(true);

      // Track payment attempt
      trackEvent(PAYMENT_EVENTS.PAYMENT_CLICKED, {
        paymentMethod: 'card',
        plan: plan,
        billedAmount: plan.billedAmount,
        isExtension: !!activeSubscription
      }); 
    }

    if (!plan.hasMonthlyMultiplePeriod) { // one-time payment, no draft sub
      // setIsProcessing(true); // no network request here
      const paymentUrl = buildCCPaymentUrl(plan, user.id);
      setCardPaymentUrl(paymentUrl);
      window.open(paymentUrl, '_blank', 'noopener,noreferrer');
      subscribeAndTrackPayment(user.id);
      // setIsProcessing(false);
      return;
    } else if (!draftSubscriptionIdRef.current || !cardPaymentUrl) {
      setIsProcessing(true);

      try {
        if (!user || !plan) throw new Error('User or plan missing');
        const draftId = await createOrUpdateDraftSubscription(plan, user.id);
        draftSubscriptionIdRef.current = draftId;
        const paymentUrl = buildCCPaymentUrl(plan, draftId);
        setCardPaymentUrl(paymentUrl);

        window.open(paymentUrl, '_blank', 'noopener,noreferrer');
        subscribeAndTrackPayment(user.id);
      } catch (error) {
        setErrorMessage('Failed to initiate payment. Please try again.');
        setShowErrorDialog(true);
        trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
          paymentMethod: 'card',
          plan: plan,
          billedAmount: plan?.billedAmount,
          isExtension: !!activeSubscription,
          error: error instanceof Error ? error.message : String(error)
        });
      } finally {
        setIsProcessing(false);
      }

      return;
    }

    window.open(cardPaymentUrl, '_blank', 'noopener,noreferrer');
    subscribeAndTrackPayment(user.id);
  };

  if (isLoading || isSessionLoading) {
    return (
      <div className="flex-auto flex flex-col">
        <div className="flex items-center justify-center flex-1">
          <div className="animate-spin size-8 border-4 border-white/20 border-t-white rounded-full" />
        </div>
      </div>
    );
  }

  const hidePaymentsOnRetrictedPlatform = isHidingMonetizationInStores()
    || (isPlayStoreInstalled && isPlayBillingSupported);
  const hideAnyOutsidePaymentCta = false; // isHidingExternalPaymentCta(storefrontCountry);
  const showOneTimeUnlimited = isPlayBillingSupported || isAppleStoreKitSupported ||
    (!hidePaymentsOnRetrictedPlatform
      && !(activeSubscription && isRenewableSubscriber)
      // && !hadPreviousSubscription
    );
  const unlimitedActiveSubscription = subscriptions.find(s => s.status === 'active'
    && s.type?.includes('unlimited')
    && s.end_at && s.end_at > new Date().toISOString());

  const lowestDailyPrice = subscriptionPlans.reduce((min, plan) => {
    if (!plan.hasMonthlyMultiplePeriod) return min;

    let monthlyPrice = plan.price;
    let currencyCode = 'USD';

    if (isAppleStoreKitSupported) {
      const appleProduct = appleStoreProducts.find(p => p.attributes?.offerName === plan.appleStoreSku);
      monthlyPrice = parseFloat(appleProduct?.attributes?.offers[0]?.price ?? '0') / plan.months || monthlyPrice;
      currencyCode = appleProduct?.attributes?.offers[0]?.currencyCode ?? currencyCode;
    }

    return {
      monthlyPrice: Math.min(min.monthlyPrice, Math.round(100 * monthlyPrice / 30.44) / 100 ),
      currencyCode,
    };
  }, { monthlyPrice: Infinity, currencyCode: 'USD' });
  const lowestDailyPriceFormatted = new Intl.NumberFormat(
    navigator?.language || 'en-US',
    {style: 'currency', currency: lowestDailyPrice.currencyCode}
  ).format(lowestDailyPrice.monthlyPrice);

  const AppleRestorePurchaseButton = () => isAppleStoreKitSupported && (
    <Button 
      className={cn(
        'py-4 bg-slate-700 hover:bg-slate-600 border border-slate-700',
        isRestoringApplePurchase === false ? 'hidden' : ''
      )}
      onClick={() => {
        setIsRestoringApplePurchase(true);
        refetchUserInfo().then(() => {
          setIsRestoringApplePurchase(false);
        });
      }}
      disabled={isRestoringApplePurchase === true}
    >
      {(isRestoringApplePurchase) ? (
        <div className="flex items-center justify-center">
          <div className="animate-spin mr-2 size-5 border-2 border-white/30 border-t-white rounded-full" />
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2">
          Restore purchase
        </div>
      )}
    </Button>
  );
  
  return (
    <div className="flex-auto bg-zinc-800 text-white">
      <SEO 
        title="Premium Subscription Plans - EveningHoney.AI"
        description="Unlock unlimited conversations, image generation, and uncensored content with our premium subscription plans. Monthly, quarterly, or yearly options with special discounts."
        ogUrl="/subscription"
      />
      <EhEndorselyScript />
      
      {/* Subscription Banner with Timer */}
      {!hidePaymentsOnRetrictedPlatform && showDiscountBanner && timeRemaining && (
        <div className="relative bg-gradient-to-r from-purple-600/90 to-pink-600/90 p-3 text-center">
          <div className="container mx-auto flex justify-center items-center gap-2">
            <Crown className="max-sm:hidden size-7 text-white mr-2" />
            <div className="max-sm:flex-[0_0_min-content] text-white font-semibold">
              {hadPreviousSubscription ? 'SUBSCRIPTION' : 'FIRST SUBSCRIPTION'}
            </div>
            <div className="ml-2 text-white/80 font-bold">UP TO 70% OFF</div>
            <div className="ml-4 flex items-center gap-2 text-white">
              <Hourglass className="h-6 w-6" />
              <div className="flex flex-col items-center">
                <span className="font-bold">{timeRemaining.minutes.toString().padStart(2, '0')}</span>
                <span className="text-xs">Min</span>
              </div>
              <span>|</span>
              <div className="flex flex-col items-center">
                <span className="font-bold">{timeRemaining.seconds.toString().padStart(2, '0')}</span>
                <span className="text-xs">sec</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex-1 container max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Enjoy more right now</h1>
          {!activeSubscription && (
            <>
              <p className="text-zinc-300">100% secure and discrete</p>
              {showOneTimeUnlimited ? (
                <p className="text-zinc-300">One-time or subscription you can cancel anytime</p>
              ) : (
                <p className="text-zinc-300">Cancel anytime</p>
              )}
            </>
          )}
        </div>

        {/* One-time Unlimited offer */}
        {showOneTimeUnlimited && (<>
        <div className="rounded-lg px-4 md:px-8 py-4 md:py-8 bg-gradient-to-br from-zinc-800/70 to-zinc-950/70 border border-zinc-800/80 shadow-lg mb-4">
          <div className="grid md:grid-cols-12 gap-6">
            {/* Left Column - Marketing */}
            <div className="w-full md:col-span-3">
              <div className="rounded-lg overflow-hidden h-full flex flex-col">
                <div className="text-center mb-3">
                  <h2 className="text-2xl font-bold text-pink-600">
                    <span>Get it all</span>
                    <br />
                    <span>without subscribing</span>
                  </h2>
                  <p className="text-white mt-2">
                    <span>All the features of our subscription </span>
                    <span className="text-pink-600 font-bold">+</span>
                    <span> unlimited images and videos for 72h</span>
                  </p>
                </div>
              </div>
            </div>
            
            {/* Center Column - Plans */}
            {hidePaymentsOnRetrictedPlatform && !(isPlayBillingSupported || isAppleStoreKitSupported) ? (
              <div className="md:col-span-6 flex items-center justify-center">
                <div className="text-center text-2xl mb-3 px-8">
                  <h2 className="text-white font-medium mb-2">
                    {`${activeSubscription
                      ? 'Use your account on our website to manage your subscription.'
                      : 'Use your account on our website to subscribe with a discount.'}`}
                  </h2>
                  {/* TODO: Add link back to website using Intent to open in browser */}
                  {/* <a
                    href="https://www.eveninghoney.ai/?utm_medium=android-app&utm_source=sub"
                    className="font-bold text-pink-600 hover:text-pink-500 underline"
                  >www.eveninghoney.ai</a> */}
                  {/* <div className="font-bold">www.eveninghoney.ai</div> */}
                </div>
              </div>
            ) : (
            <div className="md:col-span-6 flex flex-col gap-4 items-center">
              <div 
                className={cn(
                  'w-full relative rounded-lg overflow-hidden cursor-pointer transition-all duration-200 border-2 select-none',
                  // isCurrentPlan ? "bg-gradient-to-r from-amber-950/50 to-amber-900/30 border border-amber-700/30" : 
                  'bg-gradient-to-r from-zinc-800/90 to-zinc-900/90 border-pink-700/80'
                )}
                onClick={() => { 
                  if (isPlayStoreInstalled && isPlayBillingSupported) {
                    handlePlayBillingPayment();
                  } else if (isFromAppleAppstore() && isAppleStoreKitSupported) {
                    handleAppleStorePayment(unlimitedPlan);
                  } else {
                    handleOneTimePayment(unlimitedPlan);
                  }
                }}
              >
                {unlimitedActiveSubscription?.end_at ? (
                  <div className="bg-amber-600/80 py-1 px-4 text-center text-xs font-semibold text-white">
                    UNLIMITED ENABLED UNTIL {formatDate(unlimitedActiveSubscription.end_at, { hour: 'numeric', minute: 'numeric' })}
                  </div>
                ) : (
                  <div className="bg-pink-700 py-1 px-4 text-center text-xs font-semibold text-white">
                    ONE-TIME UNLIMITED, NO RENEWAL
                  </div>
                )}
                
                <div className="p-4 sm:p-6 relative">
                  <div className="flex items-center justify-between gap-1">
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        {'72h of Unlimited'}
                      </h3>
                    </div>
                    
                    <div className="flex flex-col items-end">
                      <div className="flex items-baseline">
                        <span className="text-2xl md:text-4xl font-extrabold text-white">
                          {(() => {
                            if (isPlayBillingSupported && playStoreProduct) {
                              return playStoreProduct.localizedPrice;
                            }
                            if (isAppleStoreKitSupported && appleStoreProducts.length > 0) {
                              const appleProduct = appleStoreProducts.find(p => p.attributes?.offerName === unlimitedPlan.appleStoreSku);
                              return appleProduct?.attributes?.offers[0]?.priceFormatted || '--';
                            } else if (isAppleStoreKitSupported && appleStoreProducts.length === 0) {
                              return '--'; // TODO: show a loading spinner
                            }
                            return `$${unlimitedPlan.price}`;
                          })()}
                        </span>
                        <span className="hidden lg:block ml-1 text-sm font-medium text-zinc-400">one-time</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <Button
                className={`w-full py-6 mt-2 flex items-center justify-center gap-2 ${
                  isUnlimited ? 'bg-amber-600 hover:bg-amber-500' : 'bg-pink-600 hover:bg-pink-500'
                }`}
                onClick={
                  isAwaitingStripePayment
                    ? retryCCPayment
                    : (isPlayStoreInstalled && isPlayBillingSupported
                        ? handlePlayBillingPayment
                        : isAppleStoreKitSupported
                          ? () => handleAppleStorePayment(unlimitedPlan)
                          : () => handleOneTimePayment(unlimitedPlan)
                      )
                  }
                disabled={isProcessing || (isPlayBillingSupported && !playStoreProduct) || (isAppleStoreKitSupported && appleStoreProducts.length === 0)}
                type="button"
                size="lg"
              >
                <div className="flex flex-wrap items-center justify-center gap-1">
                  {(isProcessing && (['card', 'play_billing', 'apple_store'].includes(currentPaymentMethod ?? '') || isAwaitingStripePayment)) && (
                    <div className="animate-spin h-5 w-5 border-2 border-pink-400 border-t-transparent rounded-full" />
                  )}
                  {/* <ExternalLink className={`h-4 w-4 ${hasActiveStripeSubscription ? '' : 'hidden'}`} /> */}
                  <span className="font-bold">{unlimitedActiveSubscription
                    ? 'Extend Unlimited by 72h'
                    : (isPlayStoreInstalled && isPlayBillingSupported ? 'Pay with Google Play' : isAppleStoreKitSupported ? 'Purchase 72h Unlimited' : 'Pay with card')
                  }</span>
                  {isPlayStoreInstalled && isPlayBillingSupported ? (
                    <img src="/logos/google-logo.svg" alt="Google Play" className="h-6" />
                  ) : isAppleStoreKitSupported ? (
                    false // nothing to show here, avoid showing apple pay logo to avoid rejection during review
                  ) : (
                    <>
                      <img src="/logos/visa-logo-white.svg" alt="Visa" className="h-6" />
                      <img src="/logos/mastercard-logo.svg" alt="Mastercard" className="h-6" />
                      <img src="/logos/apple-pay-logo.svg" alt="Apple Pay" className="h-4" />
                    </>
                  )}
                </div>
              </Button>
              {isAppleStoreKitSupported && (
                // According to guidelines it's up to us to restore non-renewable purchases,
                // with no receipt/restore transaction available.
                // https://developer.apple.com/documentation/storekit/restoring-purchased-products
                <AppleRestorePurchaseButton />
              )}
            </div>
            )}

            {/* Right Column - Benefits */}
            <div className="md:col-span-3">
              <div className="rounded-lg overflow-hidden h-full flex flex-col justify-center">
                <div>
                  <ul className="space-y-3">
                  <li className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-zinc-300">Unlimited, uncensored Messages</span>
                    </li>
                    <li className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-zinc-300">Unlimited Chat & Studio Images</span>
                    </li>
                    <li className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-zinc-300">Unlimited Chat Videos</span>
                    </li>
                    <li className="flex flex-col">
                      <span className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-zinc-300">50 gems for Audio, Studio Videos</span>
                      </span>             
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={cn('flex items-center my-4 select-none', hideAnyOutsidePaymentCta && 'hidden')}>
          <div className="flex-grow border-t border-zinc-700" />
          <span className="mx-4 text-zinc-400 font-semibold">OR</span>
          <div className="flex-grow border-t border-zinc-700" />
        </div>
        </>)}
        
        {/* Main content container with gradient border */}
        <div className={cn(
          'rounded-lg px-4 md:px-8 pt-4 md:pt-8 bg-gradient-to-br from-zinc-800/70 to-zinc-950/70 border border-zinc-800/80 shadow-lg',
          hideAnyOutsidePaymentCta && 'hidden'
        )}>
          <div className="grid md:grid-cols-12 gap-4 md:gap-6">
            {/* Left Column - Marketing */}
            <div className="md:col-span-3">
              <div className="rounded-lg overflow-hidden h-full flex flex-col">
                {!((activeSubscription || hadPreviousSubscription) && isRenewableSubscriber) ? (
                  <div className="text-center mb-3">
                    <h2 className="text-2xl font-bold text-pink-600">
                      <span>Get An Exclusive</span>
                      <br />
                      <span>Subscription</span>
                      <br />
                      <span>Discount Today!</span>
                    </h2>
                    <p className="text-white mt-2">
                      <span>Up to </span>
                      <span className="text-pink-600 font-bold">{`${isAppleStoreKitSupported ? '50%' : '70%'}`}</span>
                      <span> off for first subscription, or </span>
                      <span className="text-pink-600 font-bold">{`${lowestDailyPriceFormatted} per day!`}</span>
                    </p>
                  </div>
                ) : (
                  <div className="text-center mb-3">
                    <h2 className="text-2xl font-bold text-pink-600">
                      <span>Exclusive Features</span>
                    </h2>
                    <p className="text-white mt-2">
                      <span>For premium members only, starting at </span>
                      <span className="text-pink-600 font-bold">{`${lowestDailyPriceFormatted} per day!`}</span>
                    </p>
                  </div>
                )}
                
                <div className="mt-auto">
                  <img 
                    src="/images/eh_char_dress-bl.webp" 
                    alt="AI Girlfriend"
                    className="w-full h-auto hidden md:block"
                  />
                </div>
              </div>
            </div>
            
            {/* Center Column - Plans */}
            {hidePaymentsOnRetrictedPlatform ? (
              <div className="md:col-span-6 flex items-center justify-center">
                <div className="text-center text-2xl mb-3 px-8">
                  <h2 className="text-white font-medium mb-2">
                    {`${activeSubscription
                      ? 'Use your account on our website to manage your subscription.'
                      : 'Use your account on our website to subscribe with a discount.'}`}
                  </h2>
                  {/* TODO: Add link back to website using Intent to open in browser */}
                  {/* <a
                    href="https://www.eveninghoney.ai/?utm_medium=android-app&utm_source=sub"
                    className="font-bold text-pink-600 hover:text-pink-500 underline"
                  >www.eveninghoney.ai</a> */}
                  {/* <div className="font-bold">www.eveninghoney.ai</div> */}
                </div>
              </div>
            ) : (
            <div className="md:col-span-6">
              {/* Subscription Plans */}
              <div className="flex flex-col gap-4">
                {(isAppleStoreKitSupported ? appleSubscriptionPlans : subscriptionPlans).filter(p => p.hasMonthlyMultiplePeriod).map((plan) => {
                  const isCurrentPlan = activeSubscription?.plan === plan.id;
                  const isSelected = selectedPlan?.id === plan.id;
                  const appleProduct = isAppleStoreKitSupported
                    ? appleStoreProducts?.find(p => p.attributes?.offerName === plan.appleStoreSku)
                    : undefined;
                  
                  return (
                    <div 
                      key={plan.id}
                      className={cn(
                        'relative rounded-lg overflow-hidden cursor-pointer transition-all duration-200 border-2 select-none',
                        // isCurrentPlan ? "bg-gradient-to-r from-amber-950/50 to-amber-900/30 border border-amber-700/30" : 
                        isSelected ? 'bg-gradient-to-r from-zinc-800/90 to-zinc-900/90 border-pink-700/80' : 
                        'bg-zinc-900/60 border-zinc-800/50 hover:border-zinc-700/50',
                      )}
                      onClick={() => handlePlanSelect(plan)}
                    >
                      {isCurrentPlan && (
                        <div className="bg-amber-600/80 py-1 px-4 text-center text-xs font-semibold text-white">
                          CURRENT PLAN
                        </div>
                      )}

                      {!activeSubscription && plan.id === 'quarterly' && (
                        <div className="bg-pink-700 py-1 px-4 text-center text-xs font-semibold text-white">
                          MOST POPULAR
                        </div>
                      )}
                      
                      <div className="p-4 sm:p-6 relative">
                        <div className="flex items-center justify-between gap-1">
                            {!!plan.discount && plan.discount > 0 && (
                              <div
                                className="absolute top-2 right-2 inline-flex items-center mx-1 px-2 py-1 rounded-lg text-xs font-bold bg-pink-700 text-white"
                              >
                                {plan.discount}% OFF
                              </div>
                            )}
                          <div>
                            <h3 className="text-xl font-bold text-white">
                              {appleProduct?.attributes?.name || plan.name}
                            </h3>
                            {!!plan.discount && plan.discount > 0 && 'originalPrice' in plan && (
                              <div className="mt-1 text-sm text-zinc-400">
                                <span className="line-through">Was ${plan.originalPrice} per month</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex flex-col items-end">
                            <div className="flex items-baseline">
                              <span className="text-2xl md:text-4xl font-extrabold text-white">
                                {(() => {
                                  if (isAppleStoreKitSupported) {
                                    // TODO: show a loading spinner while loading apple products
                                    return appleProduct?.attributes?.offers[0]?.priceFormatted || `--`;
                                  }
                                  return `$${plan.price}`;
                                })()}
                              </span>
                              {!isAppleStoreKitSupported && (
                                <span className="ml-1 text-sm font-medium text-zinc-400">/month</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Payment Button with Crypto Icons */}
                <div className="mt-4">
                  <Button 
                    className={cn(
                      'w-full py-6',
                      (activeSubscription && isRenewableSubscriber) ? 'bg-amber-600 hover:bg-amber-500' : 'bg-slate-700 hover:bg-slate-600 border border-slate-700',
                      isAppleStoreKitSupported && 'hidden'
                    )}
                    onClick={handleCryptoPayment}
                    disabled={isProcessing || isAwaitingStripePayment}
                    size="lg"
                  >
                    {(isProcessing && currentPaymentMethod === 'crypto') ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin mr-2 size-5 border-2 border-white/30 border-t-white rounded-full" />
                        <div>Processing...</div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <div className="font-bold">{(activeSubscription && isRenewableSubscriber) ? `Extend with` : `Pay with crypto`}</div>
                        <div className="flex items-center space-x-1">
                          <img src="/logos/btc-logo.svg" alt="Bitcoin" className="h-5 w-5" />
                          <img src="/logos/eth-logo.svg" alt="Ethereum" className="h-5 w-5" />
                          <img src="/logos/ltc-logo.svg" alt="Litecoin" className="h-5 w-5" />
                          <img src="/logos/xmr-logo.svg" alt="Monero" className="h-5 w-5" />
                        </div>
                      </div>
                    )}
                  </Button>

                {/* Credit Card / Apple Store Payment Button */}
                <Button
                  className={`w-full py-6 mt-2 flex items-center justify-center gap-2 ${
                    (activeSubscription && isRenewableSubscriber) ? 'bg-slate-700 hover:bg-slate-600' : 'bg-pink-600 hover:bg-pink-500'
                  }`}
                  onClick={isAwaitingStripePayment
                    ? retryCCPayment
                    : isAppleStoreKitSupported
                      ? () => selectedPlan && handleAppleStorePayment(selectedPlan)
                      : () => handleCCPayment()}
                  disabled={isProcessing || !selectedPlan}
                  type="button"
                  size="lg"
                >
                  <div className="flex flex-wrap items-center justify-center gap-1">
                    {((isProcessing && ['card', 'apple_store'].includes(currentPaymentMethod || '')) || isAwaitingStripePayment) && (
                      <div className="animate-spin h-5 w-5 border-2 border-pink-400 border-t-transparent rounded-full" />
                    )}
                    <ExternalLink className={`h-4 w-4 ${hasActiveStripeSubscription ? '' : 'hidden'}`} />
                    <span className="font-bold">
                      {hasActiveStripeSubscription || (activeSubscription && isRenewableSubscriber)
                        ? 'Manage subscription'
                        : isAppleStoreKitSupported
                          ? `Purchase ${selectedPlan?.name}`
                          : 'Pay with card'
                      }
                    </span>
                    {!isAppleStoreKitSupported && (
                      <>
                        <img src="/logos/visa-logo-white.svg" alt="Visa" className="h-6" />
                        <img src="/logos/mastercard-logo.svg" alt="Mastercard" className="h-6" />
                        <img src="/logos/apple-pay-logo.svg" alt="Apple Pay" className="h-4" />
                      </>
                    )}
                  </div>
                </Button>

                  {isAppleStoreKitSupported && (
                    // iOS app wrapper should implement real restore.
                    // https://developer.apple.com/documentation/storekit/restoring-purchased-products
                    // Here we simply refetch existing data from our own server.
                    <div className="mt-4 flex items-center justify-center">
                      <AppleRestorePurchaseButton />
                    </div>
                  )}
                  
                  {/* Billed amount below payment button */}
                  {selectedPlan && !isAppleStoreKitSupported && (
                    <div className="text-center mt-2 text-sm text-zinc-400">
                      <span className="capitalize">{selectedPlan.period} </span>
                      <span>{`payment of $${selectedPlan.billedAmount.toFixed(2)}`}</span>
                      {(activeSubscription && isRenewableSubscriber) && extendedEndDate && (
                        <div className="mt-1 text-amber-400">
                          New expiration date: {extendedEndDate}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center mt-1 sm:mt-3 mb-1 sm:mb-6 gap-2">
                  <div className={cn(
                    'flex items-center text-green-400 text-sm',
                    isAppleStoreKitSupported && 'hidden'
                  )}>
                    <Check className="h-4 w-4 mr-1" />
                    <span>Billed as </span>
                    <strong className="mx-1">“EH”</strong>
                    <span> on your bank statement</span>
                  </div>
                  {/* <span className="mx-2 text-zinc-500">•</span> */}
                  <div className={cn(
                    'flex items-center text-green-400 text-sm',
                    isAppleStoreKitSupported && 'hidden'
                  )}>
                    <Check className="h-4 w-4 mr-1" />
                    <span>No hidden fees</span>
                  </div>
                  <div className="flex items-center text-green-400 text-sm">
                    <Check className="h-4 w-4 mr-1" />
                    <span>Cancel anytime</span>
                  </div>
                </div>
              </div>
            </div>
            )}
            
            {/* Right Column - Benefits */}
            <div className="md:col-span-3">
              <div className="rounded-lg overflow-hidden h-full flex flex-col">
                <div>
                  <h2 className="text-xl font-bold text-white mb-4">Subscription Benefits</h2>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-zinc-300">Create your own AI Companions</span>
                    </li>
                    <li className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      {/* <span className="text-sm text-zinc-300">Unlimited text messages</span> */}
                      <span className="text-sm text-zinc-300">Unlimited messages</span>
                    </li>
                    <li className="flex flex-col">
                      <span className="flex items-start">
                        <CircleCheckBig className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-zinc-300">100 FREE gems / month</span>
                      </span>
                      <span className="ml-7 text-xs text-zinc-400">Accumulate 2 months' worth if unused</span>                      
                    </li>
                    <li className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="inline-flex items-center gap-2">
                        <span className="text-sm text-zinc-300">
                          {isFromAppStores() ? (<span>More </span>) : (<span>Hot </span>)} 
                          <span> videos</span>
                        </span>
                        <span className="text-xs text-zinc-800 font-bold bg-amber-500 rounded-md px-1.5 py-1 select-none">NEW</span>
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-zinc-300">
                        {isFromAppStores() ? (<span>More </span>) : (<span>Hot </span>)} 
                        <span> images</span>
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-zinc-300">Remove image blur</span>
                    </li>
                    {/* <li className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-zinc-300">Make AI phone calls</span>
                    </li> */}
                    {/* <li className="flex items-start">
                      <CircleCheckBig className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-zinc-300">Faster response time</span>
                    </li> */}
                  </ul>
                </div>
                
                <div className="mt-auto">
                  <img 
                    src="/images/eh_char_bikini-anime.webp" 
                    alt="AI Girlfriend"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Current Subscription Info */}
        {activeSubscription && (
          <div className="mt-8 p-4 bg-zinc-900/80 rounded-lg border border-amber-800/30">
            <div className="flex items-center mb-2">
              <Crown className="h-5 w-5 text-amber-500 mr-2" />
              <h2 className="text-xl font-semibold text-white">Your Current Subscription</h2>
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center text-zinc-300 gap-2">
              <div className="flex items-center gap-4">
                <div>
                  <span className="text-zinc-400 text-sm block">Plan</span>
                  <span className="text-white font-medium capitalize">{activeSubscription.plan}</span>
                </div>
                <div>
                  <span className="text-zinc-400 text-sm block">Expires</span>
                  <span className="text-white font-medium flex items-center">
                    <Clock className="h-4 w-4 mr-1 text-amber-500" />
                    <span>{formatDate(activeSubscription.endDate)}</span>
                  </span>
                </div>
              </div>
              {activeSubscription.subMetadata?.stripe_current_period_end && (
                <div>
                  <span className="text-zinc-400 text-sm block">
                    Next card payment
                  </span>
                  <span className="text-white font-medium flex items-center">
                    <CreditCard className="h-4 w-4 mr-1 text-amber-500" />
                    <span>{formatDate(new Date(activeSubscription.subMetadata?.stripe_current_period_end * 1000))}</span>
                  </span>
                </div>
              )}
              {/* <div className="text-sm">
                <div>
                  <span>New expiration date: </span><span className="text-amber-400">
                  {extendedEndDate || formatDate(activeSubscription.endDate)}
                </span></div>
              </div> */}
            </div>
          </div>
        )}
        
        {/* Reassurance or legal links*/}
        {isFromAppStores() ? (
          <div className="flex items-center justify-center gap-4 text-zinc-400 mt-10 text-sm">
            <div className="flex items-center">
              <Link to="/legal/terms-of-service" className="bg-zinc-800 rounded-full p-2">
                Terms of Service
              </Link>
            </div>
            <div className="flex items-center">
              <Link to="/legal/privacy-policy" className="bg-zinc-800 rounded-full p-2">
                Privacy Policy
              </Link>
            </div>
          </div>
        ): (
          <div className="flex items-center justify-center gap-8 text-zinc-400 mt-10">
            <div className="flex items-center">
              <div className="bg-zinc-800 rounded-full p-2">
                <ShieldCheck className="h-10 w-10 text-zinc-300" />
              </div>
              <span>No ads, no virus</span>
            </div>
            <div className="flex items-center">
              <div className="bg-zinc-800 rounded-full p-2">
                <ShieldCheck className="h-10 w-10 text-zinc-300" />
              </div>
              <span>Highest privacy</span>
            </div>
          </div>
        )}
      </div>

      <div className="text-center text-zinc-700 text-sm mt-4 py-2">
        {`${isTwaSession() ? 'TWA' : ''} ${isAppleStoreKitSupported ? 'App Store' : ''}${storefrontCountry ?? ''} ${
          isPlayStoreInstalled ? 'Play Store' : ''} ${isPlayBillingSupported ? '(B)' : ''}`}
      </div>
      
      {/* Auth Dialog */}
      <EhAuthDialog
        open={showAuthDialog}
        onOpenChange={handleAuthDialogOpenChange}
      />
      
      {/* Success Dialog */}
      <EhPaymentSuccessDialog
        open={showSuccessDialog}
        onOpenChange={setShowSuccessDialog}
        purchaseOption={selectedPlan}
        paymentCheckStatus={paymentCheckStatus}
        subscriptionEndDate={activeSubscription?.endDate}
        onRetryVerification={currentPaymentMethod === 'crypto' ? retryPaymentCheck : undefined}
      />
      
      {/* Error Dialog */}
      <EhPaymentErrorDialog
        open={showErrorDialog}
        onOpenChange={setShowErrorDialog}
        error={errorMessage}
        onRetry={currentPaymentMethod === 'crypto'
          ? handleCryptoPayment
          : isAppleStoreKitSupported
            ? undefined
            : retryCCPayment}
      />
      
      {/* Stripe Payment Awaiting Dialog */}
      <AlertDialog open={isAwaitingStripePayment} onOpenChange={(open) => !open && cancelStripePayment()}>
        <AlertDialogContent className="sm:max-w-md bg-gradient-to-br from-zinc-900 to-zinc-950 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <div>Awaiting Payment</div>
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-300">
              <span>Please complete your payment in the opened tab, then return here. </span>
              <span>It will be billed discreetly as </span>
              <span className="font-bold">“EH” </span>
              <span>on your bank statement.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="animate-spin h-8 w-8 border-3 border-pink-400 border-t-transparent rounded-full" />
            <p className="text-white text-center text-sm">
              We'll automatically detect when your payment is processed.
            </p>
          </div>
          
          <AlertDialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={() => {
                if (selectedPlan && draftSubscriptionIdRef.current) {
                  const paymentUrl = cardPaymentUrl || buildCCPaymentUrl(selectedPlan, draftSubscriptionIdRef.current);
                  window.open(paymentUrl, '_blank', 'noopener,noreferrer');
                }
              }}
              className="bg-slate-600 hover:bg-slate-700 flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              <span>Open Payment in new tab</span>
            </Button>
            <Button
              onClick={cancelStripePayment}
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-700 hover:text-white"
            >
              <span>Cancel</span>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 